import {
  batch,
  createEffect,
  createMemo,
  createSignal,
  untrack,
  type Accessor,
} from 'solid-js';
import { useQuery, useQueryClient } from '@tanstack/solid-query';
import { useConnectProject } from '@trimble/connect-project-providers';
import { asError } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { t } from '@trimble/i18n';

import { EXPERIMENTS_KEYS, useAppContext } from '../AppContextProvider';
import type { FxWorkspaceRepository } from './fx-workspace-repository';
import type { FxWorkspace, FxWorkspaceLoadStatus } from './fx-workspace.types';
import { fxWorkspacesQueryKey, toQueryLoadStatus } from './fx-workspace-query';
import {
  readPersistedActiveWorkspace,
  writePersistedActiveWorkspace,
} from './fx-workspace-persistence';
import { createRepositoryScopeGuard } from './fx-workspace-scope';
import { createFxWorkspaceListCache } from './fx-workspace-list-cache';

export interface FxWorkspaceListApi {
  readonly isEnabled: Accessor<boolean>;
  readonly loadStatus: Accessor<FxWorkspaceLoadStatus>;
  readonly workspaces: Accessor<readonly FxWorkspace[]>;
  readonly selectedWorkspaceId: Accessor<string | undefined>;
  readonly selectedJobId: Accessor<string | undefined>;
  readonly activeWorkspaceId: Accessor<string | undefined>;
  readonly fetchKey: Accessor<string | undefined>;
  readonly repository: Accessor<FxWorkspaceRepository>;
  setSelectedWorkspaceId(id: string | undefined): void;
  setSelectedJobId(id: string | undefined): void;
  activateWorkspace(workspaceId: string): void;
  deactivateWorkspace(): void;
  isWorkspaceActive(workspaceId: string): boolean;
  createWorkspace(name: string): Promise<FxWorkspace | undefined>;
  deleteWorkspace(workspaceId: string): Promise<boolean>;
  resetRepositoryScope(): void;
}

export function useFxWorkspaceList(options: {
  repository: Accessor<FxWorkspaceRepository>;
  repositoryKey: Accessor<string>;
}): FxWorkspaceListApi {
  const appContext = useAppContext();
  const { selectedProjectId } = useConnectProject();
  const queryClient = useQueryClient();
  const logger = useLogger().getLogger('FxWorkspace');
  const scope = createRepositoryScopeGuard();

  const [selectedWorkspaceId, setSelectedWorkspaceId] = createSignal<
    string | undefined
  >(undefined);
  const [selectedJobId, setSelectedJobId] = createSignal<string | undefined>(
    undefined
  );
  const [requestedActiveWorkspaceId, setRequestedActiveWorkspaceId] =
    createSignal<string | undefined>(
      untrack(() => {
        const repositoryKey = options.repositoryKey();
        return repositoryKey === ''
          ? undefined
          : readPersistedActiveWorkspace(repositoryKey);
      })
    );

  const isEnabled = createMemo(
    () =>
      appContext.experiments()[EXPERIMENTS_KEYS['featureExtractionPOC']] ===
        true && selectedProjectId() !== undefined
  );

  const repository = createMemo(() => options.repository());

  const fetchKey = createMemo(() => {
    const repositoryKey = options.repositoryKey();
    if (!isEnabled() || repositoryKey === '') {
      return undefined;
    }

    return repositoryKey;
  });

  const loadWorkspaces = async (
    activeRepository: FxWorkspaceRepository
  ): Promise<readonly FxWorkspace[]> => {
    try {
      return await activeRepository.list();
    } catch (error: unknown) {
      logger.error('Failed to load FX workspaces', undefined, asError(error));
      throw error;
    }
  };

  const workspacesQuery = useQuery(() => {
    const key = fetchKey();
    const enabled = key !== undefined;

    return {
      enabled,
      // eslint-disable-next-line @tanstack/query/exhaustive-deps -- repository is a client instance; query identity is repositoryKey
      queryKey: enabled
        ? fxWorkspacesQueryKey(key)
        : fxWorkspacesQueryKey('disabled'),
      queryFn: () => loadWorkspaces(untrack(repository)),
      staleTime: 0,
    };
  });

  const loadStatus = createMemo((): FxWorkspaceLoadStatus => {
    if (fetchKey() === undefined) {
      return 'idle';
    }

    return toQueryLoadStatus(workspacesQuery.status);
  });

  const workspaces = createMemo(
    (): readonly FxWorkspace[] => workspacesQuery.data ?? []
  );

  const listCache = createFxWorkspaceListCache({
    fetchKey,
    workspaces,
    queryClient,
  });

  const persistActiveWorkspace = (workspaceId: string | undefined): void => {
    const repositoryKey = fetchKey();
    if (repositoryKey === undefined) {
      return;
    }

    writePersistedActiveWorkspace(repositoryKey, workspaceId);
  };

  const refetchWorkspaces = async (scopeSnapshot: number): Promise<void> => {
    try {
      await workspacesQuery.refetch({ throwOnError: false });
    } catch (error: unknown) {
      if (scope.isCurrent(scopeSnapshot)) {
        logger.error(
          'Failed to refetch FX workspaces',
          undefined,
          asError(error)
        );
      }
    }
  };

  const activeWorkspaceId = createMemo(() => {
    const requested = requestedActiveWorkspaceId();
    if (requested === undefined || loadStatus() !== 'success') {
      return requested;
    }

    return listCache.listContainsWorkspace(requested) ? requested : undefined;
  });

  createEffect(() => {
    const repositoryKey = fetchKey();
    if (repositoryKey === undefined || loadStatus() !== 'success') {
      return;
    }

    const requested = requestedActiveWorkspaceId();
    if (requested !== undefined && activeWorkspaceId() === undefined) {
      writePersistedActiveWorkspace(repositoryKey, undefined);
    }
  });

  const activateWorkspace = (workspaceId: string): void => {
    if (!listCache.listContainsWorkspace(workspaceId)) {
      return;
    }

    setRequestedActiveWorkspaceId(workspaceId);
    persistActiveWorkspace(workspaceId);
  };

  const deactivateWorkspace = (): void => {
    setRequestedActiveWorkspaceId(undefined);
    persistActiveWorkspace(undefined);
  };

  const isWorkspaceActive = (workspaceId: string): boolean => {
    return activeWorkspaceId() === workspaceId;
  };

  const createWorkspace = async (
    name: string
  ): Promise<FxWorkspace | undefined> => {
    const trimmed = name.trim();
    if (!trimmed) {
      return undefined;
    }

    const scopeSnapshot = scope.capture();
    const [enabled, activeRepository] = untrack(
      () => [isEnabled(), repository()] as const
    );

    if (!enabled) {
      return undefined;
    }

    let created: FxWorkspace;
    try {
      created = await activeRepository.create({ name: trimmed });
    } catch (error: unknown) {
      if (!scope.isCurrent(scopeSnapshot)) {
        return undefined;
      }

      logger.error(
        'Failed to create FX workspace',
        { name: trimmed },
        asError(error)
      );
      return undefined;
    }

    if (!scope.isCurrent(scopeSnapshot)) {
      return undefined;
    }

    await refetchWorkspaces(scopeSnapshot);
    if (!scope.isCurrent(scopeSnapshot)) {
      return undefined;
    }

    batch(() => {
      const listed = listCache.cachedWorkspaces();
      if (!listed.some((workspace) => workspace.id === created.id)) {
        listCache.writeCachedWorkspaces([...listed, created]);
      }

      setSelectedWorkspaceId(created.id);
      setSelectedJobId(undefined);
      activateWorkspace(created.id);
    });
    return created;
  };

  const deleteWorkspace = async (workspaceId: string): Promise<boolean> => {
    const [enabled, activeRepository] = untrack(
      () => [isEnabled(), repository()] as const
    );
    if (!enabled) {
      return false;
    }

    const scopeSnapshot = scope.capture();

    try {
      await activeRepository.delete(workspaceId);
    } catch (error: unknown) {
      if (!scope.isCurrent(scopeSnapshot)) {
        return false;
      }

      logger.error(
        'Failed to delete FX workspace',
        { workspaceId },
        asError(error)
      );
      NotificationEventBus.emit({
        type: 'error',
        message: t('mobileMapping.fxWorkspace.deleteFailed'),
      });
      return false;
    }

    if (!scope.isCurrent(scopeSnapshot)) {
      return false;
    }

    await refetchWorkspaces(scopeSnapshot);
    if (!scope.isCurrent(scopeSnapshot)) {
      return false;
    }

    batch(() => {
      listCache.writeCachedWorkspaces(
        listCache
          .cachedWorkspaces()
          .filter((workspace) => workspace.id !== workspaceId)
      );

      if (selectedWorkspaceId() === workspaceId) {
        setSelectedWorkspaceId(undefined);
        setSelectedJobId(undefined);
      }
      if (requestedActiveWorkspaceId() === workspaceId) {
        deactivateWorkspace();
      }
    });
    return true;
  };

  return {
    isEnabled,
    loadStatus,
    workspaces,
    selectedWorkspaceId,
    selectedJobId,
    activeWorkspaceId,
    fetchKey,
    repository,
    setSelectedWorkspaceId,
    setSelectedJobId,
    activateWorkspace,
    deactivateWorkspace,
    isWorkspaceActive,
    createWorkspace,
    deleteWorkspace,
    resetRepositoryScope: (): void => {
      scope.invalidate();
      setSelectedWorkspaceId(undefined);
      setSelectedJobId(undefined);
      const repositoryKey = options.repositoryKey();
      setRequestedActiveWorkspaceId(
        repositoryKey === ''
          ? undefined
          : readPersistedActiveWorkspace(repositoryKey)
      );
    },
  };
}
